import livestreamAssets from "../../data/livestream-assets.json";
import rasterAssets from "../../data/livestream-product-backgrounds.json";
import {LIVESTREAM_ARTWORK, LIVESTREAM_BGS} from "./backgrounds";

export type LivestreamTemplateId = keyof typeof LIVESTREAM_BGS;
type RasterProductCode = keyof typeof rasterAssets;
type Transform = {x: number; y: number; scale: number};
type BackgroundRecord = Record<string, string | undefined>;
type UploadRecord = Record<string, boolean | undefined>;

function readSvgDataUrl(url: string): string | null {
  const match = url.match(/^data:image\/svg\+xml;base64,([A-Za-z0-9+/=]+)$/);
  if (!match) return null;
  try {
    return new TextDecoder().decode(Uint8Array.from(atob(match[1]), (char) => char.charCodeAt(0)));
  } catch {
    return null;
  }
}

type DefaultSceneLayer = {
  product?: string | null;
  scene_data?: {
    customBgs?: BackgroundRecord;
    bgX?: Record<string, number | undefined>;
    bgY?: Record<string, number | undefined>;
    bgScale?: Record<string, number | undefined>;
  } | null;
};

export const LIVESTREAM_TEMPLATE_IDS = Object.freeze(
  Object.keys(LIVESTREAM_BGS) as LivestreamTemplateId[]
);

// These values preserve the approved placement from the original 4096 px
// component export after removing its opaque 101 px Figma frame. The source
// image is the transparent 3320 px image fill inside that component.
const DOT_NET_TRANSFORMS: Record<LivestreamTemplateId, Transform> = {
  LivestreamHorizontal: {
    x: -511.66666666666663,
    y: -616.6579632678884,
    scale: 0.8568273092369478,
  },
  LivestreamSquare: {
    x: -614.7572812617135,
    y: -595.7471677332578,
    scale: 0.995650962830336,
  },
  LivestreamBlog: {
    x: -511.66666666666663,
    y: -616.6579632678884,
    scale: 0.8568273092369478,
  },
  LivestreamWide: {
    x: -535.3456117136113,
    y: -955.2768925392342,
    scale: 0.9006820099190844,
  },
  LivestreamStoriesIntro: {
    x: -1060.2629292766082,
    y: 248.74686037579994,
    scale: 0.9637661958031525,
  },
  LivestreamStories: {
    x: -728.8124496429903,
    y: -308.883177264907,
    scale: 0.7643054466579994,
  },
  LivestreamEmail: {
    x: -519.6614583333334,
    y: -626.293243943949,
    scale: 0.8702152359437751,
  },
};

// Junie's transparent Figma frame is 4470 px wide. Scale its approved
// placement by each template's width so the artwork stays on the right and
// leaves the text area clear.
const JUNIE_SOURCE_WIDTH = rasterAssets.JN.width;
const JUNIE_TRANSFORMS: Record<LivestreamTemplateId, Transform> = {
  LivestreamHorizontal: {x: 190, y: -368, scale: 2558 / JUNIE_SOURCE_WIDTH},
  LivestreamSquare: {x: 160.3125, y: -310.5, scale: 2158.3125 / JUNIE_SOURCE_WIDTH},
  LivestreamBlog: {x: 190, y: -368, scale: 2558 / JUNIE_SOURCE_WIDTH},
  LivestreamWide: {x: 285, y: -552, scale: 3837 / JUNIE_SOURCE_WIDTH},
  LivestreamStoriesIntro: {
    x: 160.3125,
    y: -310.5,
    scale: 2158.3125 / JUNIE_SOURCE_WIDTH,
  },
  LivestreamStories: {x: 160.3125, y: -310.5, scale: 2158.3125 / JUNIE_SOURCE_WIDTH},
  LivestreamEmail: {x: 192.96875, y: -373.75, scale: 2597.96875 / JUNIE_SOURCE_WIDTH},
};

const RASTER_TRANSFORMS: Record<RasterProductCode, Record<LivestreamTemplateId, Transform>> = {
  JN: JUNIE_TRANSFORMS,
  DN: DOT_NET_TRANSFORMS,
};

function rasterAsset(productCode: string) {
  return rasterAssets[productCode as RasterProductCode];
}

export function isLivestreamRasterProduct(productCode: string): productCode is RasterProductCode {
  return Boolean(rasterAsset(productCode));
}

export function getLivestreamFallbackBackground(
  productCode: string,
  templateId: LivestreamTemplateId
): string {
  return rasterAsset(productCode)?.url || LIVESTREAM_BGS[templateId];
}

export function getLivestreamSeedBackgrounds(productCode: string): BackgroundRecord {
  const asset = rasterAsset(productCode);
  if (!asset) return {...LIVESTREAM_BGS};
  return Object.fromEntries([
    ["main", asset.url],
    ...LIVESTREAM_TEMPLATE_IDS.map((templateId) => [templateId, asset.url]),
  ]);
}

/**
 * Replace an inherited Livestream background with the raster product artwork.
 * Keep a slot when the user uploaded a background for that slot.
 */
export function normalizeLivestreamRasterBackgrounds(
  productCode: string,
  customBgs: BackgroundRecord,
  customBgUploaded: UploadRecord
): BackgroundRecord {
  const asset = rasterAsset(productCode);
  if (!asset) return customBgs;

  let changed = false;
  const next = {...customBgs};
  const hasSharedUpload =
    Boolean(customBgs.main) &&
    LIVESTREAM_TEMPLATE_IDS.every((templateId) => customBgUploaded[templateId] === true);

  for (const id of ["main", ...LIVESTREAM_TEMPLATE_IDS]) {
    const hasExplicitUpload = id === "main" ? hasSharedUpload : customBgUploaded[id] === true;
    if (hasExplicitUpload || next[id] === asset.url) continue;
    next[id] = asset.url;
    changed = true;
  }

  return changed ? next : customBgs;
}

/**
 * A wildcard Livestream scene contains transforms for the SVG artwork. It
 * cannot crop Junie or .NET PNG artwork. Product-scoped source and transform
 * ownership can come from separate cascade layers.
 */
export function shouldUseLivestreamResolvedTransforms(
  productCode: string,
  layers: DefaultSceneLayer[] | null | undefined,
  templateId: string
): boolean {
  const asset = rasterAsset(productCode);
  if (!asset) return true;
  const productLayers = (layers ?? []).filter((layer) => layer.product === productCode);
  const ownsAsset = productLayers.some(
    (layer) => layer.scene_data?.customBgs?.[templateId] === asset.url
  );
  const ownsTransform = productLayers.some((layer) => {
    const scene = layer.scene_data;
    return (
      typeof scene?.bgX?.[templateId] === "number" &&
      typeof scene.bgY?.[templateId] === "number" &&
      typeof scene.bgScale?.[templateId] === "number"
    );
  });
  return ownsAsset && ownsTransform;
}

export function getLivestreamDefaultTransform(
  productCode: string,
  templateId: LivestreamTemplateId
): Transform | undefined {
  return RASTER_TRANSFORMS[productCode as RasterProductCode]?.[templateId];
}

export function getLivestreamSelectorBackground(
  productCode: string,
  customBgs: BackgroundRecord = {}
): {
  src: string;
  fullSrc: string | undefined;
} {
  const asset = rasterAsset(productCode);
  if (asset) return {src: asset.thumbnailUrl, fullSrc: asset.url};
  const currentSvg = customBgs.LivestreamHorizontal;
  if (currentSvg?.startsWith("data:image/svg+xml")) {
    const currentMarkup = readSvgDataUrl(currentSvg);
    const artworkMarkup = readSvgDataUrl(LIVESTREAM_ARTWORK);
    if (currentMarkup && artworkMarkup) {
      const parser = new DOMParser();
      const currentDoc = parser.parseFromString(currentMarkup, "image/svg+xml");
      const artworkDoc = parser.parseFromString(artworkMarkup, "image/svg+xml");
      const gradients = "linearGradient, radialGradient";
      const sourceStops = currentDoc.querySelector(gradients)?.querySelectorAll("stop");
      if (sourceStops?.length && !currentDoc.querySelector("parsererror")) {
        artworkDoc.querySelectorAll(gradients).forEach((gradient) => {
          gradient.replaceChildren(
            ...Array.from(sourceStops, (source) => {
              const stop = artworkDoc.createElementNS("http://www.w3.org/2000/svg", "stop");
              for (const name of ["offset", "stop-color", "stop-opacity", "style"]) {
                const value = source.getAttribute(name);
                if (value !== null) stop.setAttribute(name, value);
              }
              return stop;
            })
          );
        });
        const serialized = new XMLSerializer().serializeToString(artworkDoc.documentElement);
        const bytes = new TextEncoder().encode(serialized);
        let binary = "";
        for (const byte of bytes) binary += String.fromCharCode(byte);
        const preview = `data:image/svg+xml;base64,${btoa(binary)}`;
        return {src: preview, fullSrc: currentSvg};
      }
    }
  }
  return {src: LIVESTREAM_ARTWORK, fullSrc: livestreamAssets.artwork?.url};
}
